import { Suspense, lazy, useEffect, useState } from 'react';
import { initBridge } from '@/platform';
import { seedBuiltinProtocols } from '@/data/repositories/protocols';
import { useApp } from '@/state/store';
import { Calibration } from '@/screens/Calibration';
import { Home } from '@/screens/Home';
import { SessionRunner } from '@/screens/SessionRunner';
import { Researcher } from '@/screens/Researcher';
import { Tutorial } from '@/screens/Tutorial';
import { AppShell } from '@/components/AppShell';
import { Interpretation } from '@/screens/Interpretation';
import { Stories } from '@/screens/Stories';
import { Roleplay } from '@/screens/Roleplay';
import { Records } from '@/screens/Records';
import { upgradeLegacyScores } from '@/data/repositories/scores';

const Journey = lazy(() => import('@/screens/Journey').then(module => ({ default: module.Journey })));
const Dashboard = lazy(() => import('@/screens/Dashboard').then(module => ({ default: module.Dashboard })));

export function App() {
  const [bootError, setBootError] = useState(false);
  const screen = useApp((s) => s.screen);
  const status = useApp(s => s.session.status);
  const episodeId = useApp(s => s.episodeId);
  const go = useApp((s) => s.go);
  const setBridgeReady = useApp((s) => s.setBridgeReady);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [screen]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([initBridge(), seedBuiltinProtocols(), upgradeLegacyScores()]).then(() => {
      if (cancelled) return;
      setBridgeReady(true);
      go('home');
    }).catch(() => { if (!cancelled) setBootError(true); });
    return () => { cancelled = true; };
  }, [go, setBridgeReady]);

  if (screen === 'boot') {
    if (bootError) return <div className="page"><p role="alert">저장 기록을 준비하지 못했습니다. 다른 탭을 닫고 다시 열어 주세요.</p><button className="button button-solid mt-4" onClick={() => window.location.reload()}>다시 불러오기</button></div>;
    return (
      <div className="grid min-h-full place-items-center">
        <span className="font-mono text-[12px] tracking-[0.14em] text-[var(--color-screen-dim)]">
          CBMI
        </span>
      </div>
    );
  }
  if (screen === 'journey') return <Suspense fallback={<div className="page">이야기를 준비하고 있어요…</div>}><Journey /></Suspense>;
  if (screen === 'roleplay') return <Roleplay key={episodeId} />;
  const content = screen === 'calibration' ? <Calibration /> :
    screen === 'session' ? <SessionRunner /> :
    screen === 'dashboard' ? <Dashboard /> :
    screen === 'researcher' ? <Researcher /> :
    screen === 'tutorial' ? <Tutorial /> :
    screen === 'interpretation' ? <Interpretation /> :
    screen === 'stories' ? <Stories /> :
    screen === 'records' ? <Records /> : <Home />;
  // Keep SessionRunner mounted across setup, trial execution and scoring.
  return <div className={screen === 'session' && (status === 'running' || status === 'preparing') ? 'session-immersive' : ''}><AppShell><Suspense fallback={<div className="page" role="status">화면을 준비하고 있어요…</div>}>{content}</Suspense></AppShell></div>;
}
